import type { TodayShortBannerProps } from './types';
import { Flex, RemoteImage, Typography, useScreenSize } from '@banhala/ui-kit';
import type Animated from 'react-native-reanimated';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnyObject } from '../../../../types';
import type { TodayShortBannerItem } from './types';
import {
  useOpenDeeplink,
  useForegroundInterval,
  useSafeTimeout,
} from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { BannerScrollView } from '../BannerScrollView';

const AUTO_SCROLL_INTERVAL = 5000;

const useCountdownTimer = (finishedAt: string | null) => {
  const [timeText, setTimeText] = useState<string | null>(null);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    setIsFinished(false);
    if (!finishedAt) {
      setTimeText(null);
    }
  }, [finishedAt]);

  useForegroundInterval(
    () => {
      if (!finishedAt) return;
      const diff = new Date(finishedAt).getTime() - Date.now();

      if (diff <= 0) {
        setTimeText('00:00:00.00');
        setIsFinished(true);
        return;
      }

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff / 60000) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      const ms = Math.floor((diff % 1000) / 10);

      setTimeText(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(ms).padStart(2, '0')}`,
      );
    },
    finishedAt && !isFinished ? 10 : null,
    { immediate: true },
  );

  return timeText;
};

export const TodayShortBanner: React.FC<TodayShortBannerProps> = ({
  body,
  entity,
  logging,
  isVisible = true,
}) => {
  const { width: screenWidth } = useScreenSize();

  const ratio = body.item.ratio;
  const bannerHeight = screenWidth / ratio;

  const scrollRef = useRef<Animated.ScrollView>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const displayedIndicesRef = useRef<Set<number>>(new Set());

  const itemCount = entity.length;
  const isInfinite = itemCount > 1;
  const { openDeeplink } = useOpenDeeplink();
  const setSafeTimeout = useSafeTimeout();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const timeText = useCountdownTimer(body.item.finished_at);

  // 무한 스크롤: [마지막, ...원본, 첫번째]
  const infiniteItems = isInfinite
    ? [entity[itemCount - 1], ...entity, entity[0]]
    : entity;

  useEffect(() => {
    if (isInfinite) {
      scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
    }
  }, [isInfinite, screenWidth]);

  useEffect(() => {
    displayedIndicesRef.current = new Set();
  }, [entity]);

  // 디스플레이 로깅
  useEffect(() => {
    if (itemCount === 0) return;
    if (displayedIndicesRef.current.has(currentIndex)) return;
    displayedIndicesRef.current.add(currentIndex);

    const bannerItem = entity[currentIndex];
    if (!bannerItem) return;

    logAnalyticsEvent({
      name: 'VIEW_BANNER',
      analytics: {
        ...logging.analytics,
        ...bannerItem.logging.analytics,
      },
      ably_only_log: {
        ...(logging.ably_only_log as AnyObject),
        ...(bannerItem.logging.ably_only_log as AnyObject),
      },
    });
  }, [currentIndex, entity, itemCount, logging, logAnalyticsEvent]);

  // 자동 롤링
  useForegroundInterval(
    () => {
      const nextRealIndex = currentIndexRef.current + 1;
      const scrollTarget = (nextRealIndex + 1) * screenWidth;
      scrollRef.current?.scrollTo({ x: scrollTarget, animated: true });

      if (nextRealIndex >= itemCount) {
        setSafeTimeout(() => {
          scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
        }, 400);
        currentIndexRef.current = 0;
        setCurrentIndex(0);
      } else {
        currentIndexRef.current = nextRealIndex;
        setCurrentIndex(nextRealIndex);
      }
    },
    isVisible && itemCount > 1 && !isDragging ? AUTO_SCROLL_INTERVAL : null,
    { immediate: false },
  );

  const handleScrollBeginDrag = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleScrollEndDrag = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 정착 시: 무한 clone 경계 점프 + currentIndex 갱신.
  // snap=true(web)면 최근접 페이지로 정렬(scrollTo)까지.
  const settle = useCallback(
    (offsetX: number, snap: boolean) => {
      const scrollIndex = Math.round(offsetX / screenWidth);

      if (isInfinite) {
        if (scrollIndex === 0) {
          scrollRef.current?.scrollTo({
            x: itemCount * screenWidth,
            animated: false,
          });
          currentIndexRef.current = itemCount - 1;
          setCurrentIndex(itemCount - 1);
          return;
        }
        if (scrollIndex === itemCount + 1) {
          scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
          currentIndexRef.current = 0;
          setCurrentIndex(0);
          return;
        }
        if (snap) {
          scrollRef.current?.scrollTo({
            x: scrollIndex * screenWidth,
            animated: true,
          });
        }
        const realIndex = scrollIndex - 1;
        currentIndexRef.current = realIndex;
        setCurrentIndex(realIndex);
      } else {
        if (snap) {
          scrollRef.current?.scrollTo({
            x: scrollIndex * screenWidth,
            animated: true,
          });
        }
        currentIndexRef.current = scrollIndex;
        setCurrentIndex(scrollIndex);
      }
    },
    [screenWidth, isInfinite, itemCount],
  );

  const handleBannerPress = useCallback(
    (item: TodayShortBannerItem) => {
      if (!item.item.deeplink) return;
      logAnalyticsEvent({
        name: 'CLICK_SUB_BANNER',
        analytics: {
          ...logging.analytics,
          ...item.logging.analytics,
        },
        ably_only_log: {
          ...(logging.ably_only_log as AnyObject),
          ...(item.logging.ably_only_log as AnyObject),
        },
      });
      openDeeplink(item.item.deeplink);
    },
    [openDeeplink, logging, logAnalyticsEvent],
  );

  return (
    <Flex>
      <BannerScrollView
        scrollRef={scrollRef}
        pageWidth={screenWidth}
        onSettle={settle}
        onDragStart={handleScrollBeginDrag}
        onDragEnd={handleScrollEndDrag}
      >
        {infiniteItems.map((bannerItem, index) => (
          <Flex key={index} onPress={() => handleBannerPress(bannerItem)}>
            <RemoteImage
              uri={bannerItem.item.image_url}
              width={screenWidth}
              height={bannerHeight}
            />
          </Flex>
        ))}
      </BannerScrollView>
      {timeText !== null && (
        <Flex
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          width={120}
          justifyContent="center"
        >
          <Typography type="subtitle3" color="content_pink_primary">
            {timeText}
          </Typography>
        </Flex>
      )}
      {isInfinite && (
        <Flex.Row
          alignItems="center"
          position="absolute"
          bottom={16}
          right={16}
          backgroundColor="alpha_dark40"
          borderRadius={12}
          padding={{
            vertical: 4,
            horizontal: 10,
          }}
        >
          <Typography type="subtitle3" color="content_always_white">
            {currentIndex + 1}
          </Typography>
          <Typography type="body3" color="content_always_white">
            /
          </Typography>
          <Typography type="body3" color="content_always_white">
            {itemCount}
          </Typography>
        </Flex.Row>
      )}
    </Flex>
  );
};
